<script setup>
import { useForm } from "@/form";
import { DownloadIcon, LoaderCircleIcon, RefreshCwIcon } from "lucide-vue-next";
import { inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import Tooltip from "@Components/Tooltip.vue";
import { FormModal, Modal } from "@Components/Modal";
import { FormTextarea } from "@Components/Form";
import { PrimaryButton, SecondaryButton } from "@Components/Button";

const props = defineProps({
    src: String,
    title: String,
    prompt: String,
    uid: String,
    isThumbnail: {
        type: Boolean,
        default: false,
    },
});

const { t: __ } = useI18n();

const generation = inject("generation");
const updateGeneration = inject("updateGeneration");

const form = useForm({
    id: generation.id,
    media_id: props.uid,
    is_thumbnail: props.isThumbnail,
});

const updatePromptForm = useForm({
    id: generation.id,
    media_id: props.uid,
    is_thumbnail: props.isThumbnail,
    prompt: props.prompt,
});

const downloadImage = async () => {
    const image = await fetch(props.src);
    const imageBlog = await image.blob();
    const imageURL = URL.createObjectURL(imageBlog);

    const link = document.createElement("a");
    link.href = imageURL;
    link.download = (props.title ?? "image") + ".png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const regenerateImage = async () => {
    if (form.processing) {
        return;
    }

    const response = await form.post("single_mode_regenerate_image");

    updateGeneration(response);
};

const promptModal = ref();

const updatePrompt = async () => {
    if (updatePromptForm.processing) {
        return;
    }

    await updatePromptForm.post("single_mode_update_image_prompt");

    promptModal.value.hide();

    regenerateImage();
};
</script>
<template>
    <div class="relative group">
        <img :src="src" />
        <template v-if="!form.processing">
            <div class="not-prose absolute top-4 right-4 flex items-center space-x-3">
                <Tooltip :content="__('Régénérer')" placement="top">
                    <button @click="regenerateImage" class="bg-opacity-50 flex justify-center items-center p-2 bg-gray-900 rounded-md hover:bg-opacity-75">
                        <RefreshCwIcon class="size-4 text-white" />
                    </button>
                </Tooltip>
                <Tooltip :content="__('Télécharger')" placement="top">
                    <button @click="downloadImage" class="bg-opacity-50 flex justify-center items-center p-2 bg-gray-900 rounded-md hover:bg-opacity-75">
                        <DownloadIcon class="size-4 text-white" />
                    </button>
                </Tooltip>
            </div>
            <div v-if="props.prompt" class="not-prose absolute right-4 bottom-4 flex items-center space-x-3">
                <Tooltip :content="__('Modifier le prompt')" placement="top">
                    <button
                        @click="$refs['promptModal'].show()"
                        class="bg-opacity-50 flex justify-center items-center p-2 text-sm text-white bg-gray-900 rounded-md dark:text-white hover:bg-opacity-75">
                        {{ __("Prompt") }}
                    </button>
                </Tooltip>
            </div>
        </template>
        <template v-else>
            <div
                class="not-prose w-full h-full absolute inset-0 flex flex-col justify-center items-center text-gray-900 bg-white opacity-80 dark:text-white dark:bg-gray-950">
                <LoaderCircleIcon class="size-6 animate-spin" />
                <span class="mt-3 text-sm">{{ __("Image en cours de génération...") }}</span>
            </div>
        </template>

        <FormModal ref="promptModal" :title="__('Prompt de génération')" @submit="updatePrompt">
            <template #body>
                <FormTextarea
                    :z="1"
                    v-model="updatePromptForm.prompt"
                    :label="__('Prompt')"
                    :errors="updatePromptForm.getErrors('prompt')"
                    :rows="5"
                    :left-actions="[
                        {
                            id: 'magic-prompt',
                        },
                    ]" />
            </template>
            <template #actions>
                <div class="flex justify-end items-center space-x-3">
                    <SecondaryButton :z="1" :label="__('Annuler')" @click="promptModal.hide()">
                        {{ __("Annuler") }}
                    </SecondaryButton>
                    <PrimaryButton
                        :z="1"
                        type="submit"
                        :processing="updatePromptForm.processing"
                        :disabled="updatePromptForm.processing"
                        :success="updatePromptForm.recentlySuccessful">
                        {{ __("Générer") }}
                    </PrimaryButton>
                </div>
            </template>
        </FormModal>
    </div>
</template>
